import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { scrollToId } from "../lib/site";
import { Reveal, ChapterTag, SectionTitle } from "./Reveal";

const FLOW = ["Konsultasi kebutuhan", "Survey lokasi", "Perencanaan sistem", "Pembuatan panel", "Instalasi refrigeration", "Testing & commissioning", "Maintenance & after sales"];

function Counter({ value, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, Number(value) || 0, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <span ref={ref} className="font-display text-5xl sm:text-6xl font-bold text-ice" data-testid={`stat-${(value ?? "")}-${suffix || ""}`}>
      {display}
      {suffix}
    </span>
  );
}

export function About() {
  const { content } = useSite();
  const stats = content?.stats || [];
  const aboutImg = content?.settings?.about_image;

  return (
    <section id="tentang" data-testid="about-section" className="relative py-24 sm:py-32 bg-navy-950">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterTag number="01" label="Tentang Kami" />
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          <div>
            <SectionTitle>
              Partner Terpercaya untuk <span className="text-ice">Cold Storage</span> Anda
            </SectionTitle>
            <Reveal delay={0.15}>
              <p className="mt-6 text-steel text-base sm:text-lg leading-relaxed">
                Kami hadir memberikan solusi penyimpanan bersuhu rendah yang dapat disesuaikan dengan kebutuhan setiap bisnis. Kami mengutamakan kualitas material, efisiensi sistem pendingin, ketepatan pemasangan, serta pelayanan yang responsif.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-8 space-y-3">
                {FLOW.map((f, i) => (
                  <li key={f} className="flex items-center gap-4 group">
                    <span className="font-mono text-xs text-ice/70 w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span className="h-px w-6 bg-glacier/30 group-hover:w-10 group-hover:bg-ice/60 transition-all duration-300" />
                    <span className="text-sm text-frost/90">{f}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                data-testid="about-cta"
                onClick={() => scrollToId("penawaran")}
                className="mt-9 inline-flex items-center gap-2 border border-ice/50 text-ice font-semibold px-6 py-3.5 rounded-sm hover:bg-ice hover:text-navy-950 transition-colors duration-200"
              >
                Diskusikan Kebutuhan Anda
                <ArrowRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="relative">
            <div className="relative">
              <div className="absolute -inset-3 border border-glacier/25 rounded-sm" />
              {aboutImg && (
                <img src={aboutImg} alt="Teknisi melakukan instalasi cold storage" loading="lazy" className="relative w-full aspect-[4/3] object-cover rounded-sm" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent rounded-sm" />
              <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] text-ice/90 uppercase">Field Installation — Refrigeration Unit</div>
            </div>
          </Reveal>
        </div>

        {stats.length > 0 && (
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-glacier/15 border border-glacier/15 rounded-sm overflow-hidden" data-testid="stats-grid">
            {stats.map((s, i) => (
              <Reveal key={s.id || i} delay={0.08 * i} className="bg-navy-900 p-7 sm:p-9">
                <Counter value={s.value} suffix={s.suffix} />
                <p className="mt-2 text-sm text-steel">{s.label}</p>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
